import { inject, provide, ref } from "vue";
import { useStore } from "vuex";

import { AccountType, QuickbooksCompany } from "./types";

export const injectKey = Symbol();

export function useQuickbooksConfig(provideState = false) {
  const store = useStore();

  const fetchingCompanies = ref(false);
  const companies = ref<QuickbooksCompany[]>([]);

  const fetchingAccountTypes = ref(false);
  const accountTypes = ref<AccountType[]>([]);

  const inOAuthFlow = ref(false);

  const refreshingSettings = ref(false);

  async function getOAuthURL() {
    return store.dispatch("getOAuthURLForQuickbooks");
  }

  async function fetchTenant() {
    return store.dispatch("fetchTenant");
  }

  async function fetchQuickbooksCompanies() {
    fetchingCompanies.value = true;
    companies.value = await store.dispatch("getQuickbooksCompanies");
    fetchingCompanies.value = false;
  }

  async function fetchQuickbooksAccountTypes() {
    fetchingAccountTypes.value = true;
    accountTypes.value = await store.dispatch("getQuickbooksAccountTypes");
    fetchingAccountTypes.value = false;
  }

  async function saveAccount(
    company_id,
    field,
    account_name
  ): Promise<QuickbooksCompany> {
    const data = await store.dispatch("saveAccount", {
      company_id,
      field,
      account_name
    });

    await fetchQuickbooksCompanies();

    return data.data;
  }

  async function saveRefundSyncStrategy(
    company_id,
    refund_sync_strategy
  ): Promise<QuickbooksCompany> {
    const data = await store.dispatch("saveRefundSyncStrategy", {
      company_id,
      refund_sync_strategy
    });

    await fetchQuickbooksCompanies();

    return data.data;
  }

  async function unlinkCompany(company: QuickbooksCompany) {
    await store.dispatch("unlinkQuickbooksCompany", company.id);
    await fetchQuickbooksCompanies();
  }

  async function refreshSettings(company: QuickbooksCompany) {
    refreshingSettings.value = true;

    try {
      await store.dispatch("refreshQuickbooksCompany", company.id);
      await fetchQuickbooksCompanies();
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }

    refreshingSettings.value = false;
  }

  async function multipleQboFeatureFlag() {
    return store.getters.featureEnabledForTenant("multiple_qbo");
  }

  async function linkCompany() {
    try {
      const connectUrl = await getOAuthURL();
      inOAuthFlow.value = true;
      await launchConnectWindow(connectUrl);
      await fetchQuickbooksCompanies();
      await fetchTenant();
      inOAuthFlow.value = false;
    } catch (e) {
      window.notify.error("Unable to load Quickbooks OAuth URL");
    }
  }

  // Taken from the docs: https://github.com/intuit/oauth-jsclient
  // A little silly we have to use an interval to wait for the window to close,
  // but this has to be like this because of cross domain.
  async function launchConnectWindow(url: string) {
    const width = 800;
    const height = 650;
    const parameters = [
      `location=1`,
      `width=${width}`,
      `height=${height}`,
      `left=${(screen.width - width) / 2}`,
      `top=${(screen.height - height) / 2}`
    ];
    const connectionWindow = window.open(
      url,
      "connectPopup",
      parameters.join(",")
    )!;
    return new Promise((resolve) => {
      const pollOAuth = window.setInterval(function () {
        if (connectionWindow.closed) {
          clearInterval(pollOAuth);
          resolve(void 0);
        }
      }, 100);
    });
  }

  const quickbooksConfigInterface = {
    inOAuthFlow,
    quickbooksCompanies: companies,
    fetchingCompanies,
    fetchQuickbooksCompanies,
    accountTypes,
    fetchingAccountTypes,
    fetchQuickbooksAccountTypes,
    multipleQboFeatureFlag,
    unlinkCompany,
    refreshSettings,
    refreshingSettings,
    linkCompany,
    saveAccount,
    saveRefundSyncStrategy
  };

  if (provideState) provide(injectKey, quickbooksConfigInterface);
  return quickbooksConfigInterface;
}

export const injectUseQuickbooksConfig = () =>
  inject<ReturnType<typeof useQuickbooksConfig>>(injectKey)!;
